import { TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { FiEdit, FiTrash2, FiXCircle } from "react-icons/fi";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { SubscriptionInterface } from "../../common/interfaces";
import { truncateText } from "../../common/utils/formatters";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomTooltip from "../../components/CustomTooltip";
import SetPaymentCreateModal from "../../components/modal/SetPaymentCreateModal";
import PaginationControls from "../../components/PaginationControls";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { TextFeldUserSearch } from "../users/Users.styles";
import {
  ActionButton,
  Container,
  ProductSearchCard,
  Table,
  TableContainer,
  Title
} from "./subscription_config.styled";

export const Subscription = () => {
  const [subscriptions, setSubscriptions] = useState<SubscriptionInterface[]>(
    []
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionInterface | null>(null);

  // Cancelar e excluir compartilham o mesmo diálogo de confirmação; o `type`
  // decide o texto e a rota chamada.
  const [pendingAction, setPendingAction] = useState<{
    type: "cancel" | "delete";
    subscription: SubscriptionInterface;
  } | null>(null);

  const { isAdmin } = useAuth();

  const containerId = "toaster-subscription-page";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const fetchSubscriptions = async (currentPage: number) => {
    try {
      const response = await api.get("/subscription", {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          initial_date: startDate || undefined,
          final_date: endDate || undefined
        }
      });

      setSubscriptions(response.data.items);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      console.error("Erro ao buscar assinaturas:", error);
      handleHttpError(error);
    }
  };

  useEffect(() => {
    fetchSubscriptions(page);
  }, [page, search, startDate, endDate]);

  const handleOpenEdit = (subscription: SubscriptionInterface) => {
    setSelectedSubscription(subscription);
    setModalOpen(true);
  };

  const handleSubscriptionSaved = () => {
    fetchSubscriptions(page);
  };

  const handleCancel = async (subscription: SubscriptionInterface) => {
    try {
      await api.patch(`/subscription/${subscription.subscription_id}/cancel`);
      autoSuccess("Mensalidade cancelada com sucesso.");
      fetchSubscriptions(page);
    } catch (error: any) {
      console.error("Erro ao cancelar mensalidade:", error);
      handleHttpError(error);
    } finally {
      setPendingAction(null);
    }
  };

  const handleDelete = async (subscription: SubscriptionInterface) => {
    try {
      await api.delete(`/subscription/${subscription.subscription_id}`);
      autoSuccess("Mensalidade excluída com sucesso.");
      fetchSubscriptions(page);
    } catch (error: any) {
      console.error("Erro ao excluir mensalidade:", error);
      handleHttpError(error);
    } finally {
      setPendingAction(null);
    }
  };

  const handleConfirmAction = () => {
    if (!pendingAction) return;
    if (pendingAction.type === "cancel") {
      handleCancel(pendingAction.subscription);
    } else {
      handleDelete(pendingAction.subscription);
    }
  };

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}
      >
        <Title>Mensalidades</Title>
      </div>

      {isAdmin && (
        <ProductSearchCard>
          <TextField
            label="Data de Início"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toLocaleDateString("en-CA") }}
          />

          <TextField
            disabled={!startDate}
            label="Data Final"
            type="date"
            value={startDate ? endDate : ""}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: startDate || undefined,
              max: new Date().toLocaleDateString("en-CA")
            }}
          />

          <TextFeldUserSearch
            id="subscription_search"
            label="Buscar por empresa ou CNPJ"
            variant="outlined"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </ProductSearchCard>
      )}

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Empresa</th>
              <th>CNPJ</th>
              <th>Valor Pago</th>
              <th>Data Vencimento</th>
              <th>Data Pagamento</th>
              <th>Status Dívida</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((subscription) => {
              const dueDate = new Date(subscription.due_date);
              const paymentDate = subscription.payment_date
                ? new Date(subscription.payment_date)
                : null;
              const isCanceled = subscription.status === "CANCELED";
              const companyName = subscription.company?.company_name || "N/A";

              return (
                <tr key={subscription.subscription_id}>
                  {/* title guarda o nome inteiro, que a coluna corta em 20 */}
                  <td title={companyName}>{truncateText(companyName)}</td>
                  <td>{subscription.company?.company_cnpj || "N/A"}</td>
                  <td>
                    R$ {parseFloat(`${subscription.paid_amount}`).toFixed(2)}
                  </td>
                  <td>{dueDate.toLocaleDateString("pt-BR")}</td>
                  <td>
                    {paymentDate
                      ? paymentDate.toLocaleDateString("pt-BR")
                      : "Não pago"}
                  </td>
                  <td>
                    <div
                      style={{
                        width: "90%",
                        margin: "0 auto",
                        backgroundColor: paymentDate
                          ? "#228B22"
                          : isCanceled
                          ? "#64748b"
                          : (() => {
                              const now = new Date();
                              const daysDiff = Math.floor(
                                (now.getTime() - dueDate.getTime()) /
                                  (1000 * 60 * 60 * 24)
                              );

                              if (daysDiff > 30) {
                                return "#8B0000";
                              }
                              if (subscription.is_in_debt) {
                                return "#FF6347";
                              }
                              return "#FFA500";
                            })(),
                        color: "#fff",
                        borderRadius: "12px",
                        textAlign: "center",
                        padding: "4px 0",
                        fontSize: "0.9rem",
                        fontWeight: 500
                      }}
                    >
                      {paymentDate
                        ? "Pago"
                        : isCanceled
                        ? "Cancelada"
                        : (() => {
                            const now = new Date();
                            const daysDiff = Math.floor(
                              (now.getTime() - dueDate.getTime()) /
                                (1000 * 60 * 60 * 24)
                            );

                            if (daysDiff > 30) {
                              return "Em Atraso";
                            }
                            if (subscription.is_in_debt) {
                              return "Em Dívida";
                            }
                            return "Pendente";
                          })()}
                    </div>
                  </td>

                  <td style={{ minWidth: 150 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 12,
                        flexWrap: "nowrap"
                      }}
                    >
                      {!isCanceled && (
                        <CustomTooltip title="Registrar Pagamento">
                          <ActionButton
                            onClick={() => handleOpenEdit(subscription)}
                          >
                            <FiEdit size={20} color="#004336" />
                          </ActionButton>
                        </CustomTooltip>
                      )}
                      {!paymentDate && !isCanceled && (
                        <CustomTooltip title="Cancelar mensalidade">
                          <ActionButton
                            onClick={() =>
                              setPendingAction({ type: "cancel", subscription })
                            }
                          >
                            <FiXCircle size={20} color="#f59e0b" />
                          </ActionButton>
                        </CustomTooltip>
                      )}
                      <CustomTooltip title="Excluir mensalidade">
                        <ActionButton
                          onClick={() =>
                            setPendingAction({ type: "delete", subscription })
                          }
                        >
                          <FiTrash2 size={20} color="#ef4444" />
                        </ActionButton>
                      </CustomTooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <SetPaymentCreateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubscriptionSaved={handleSubscriptionSaved}
        subscription={selectedSubscription}
      />

      <ConfirmDialog
        open={pendingAction !== null}
        title={
          pendingAction?.type === "cancel"
            ? "Cancelar mensalidade"
            : "Excluir mensalidade"
        }
        description={
          pendingAction?.type === "cancel"
            ? `Deseja cancelar a mensalidade de "${
                pendingAction.subscription.company?.company_name || "empresa"
              }"? Ela sai das cobranças e dos avisos, mas continua no histórico como cancelada.`
            : `Deseja excluir definitivamente a mensalidade de "${
                pendingAction?.subscription.company?.company_name || "empresa"
              }"? Esta ação não pode ser desfeita.`
        }
        confirmText={
          pendingAction?.type === "cancel" ? "Cancelar mensalidade" : "Excluir"
        }
        cancelText="Voltar"
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
      />
    </Container>
  );
};
